import {ServiceBase} from "../ServiceBase";
import {IFreeplayVouchersService} from "./freeplay-vouchers.service.interface";
import {action, computed, observable} from "mobx";
import {IServiceFactoryExtended} from "../service-factory-extended.interface";
import {IconTitleTextTooltip} from "../../components/tooltip/tooltips/icon-title-text/icon-title-text.tooltip";
import React from "react";
import {
    FREE_PLAY_TOOLTIP_DISPLAYED_NUMBER,
    FREE_PLAY_TOOLTIP_IS_DISPLAYED__IN_SESSION
} from "./freeplay-vouchers-service-consts";

export class FreeplayVouchersService extends ServiceBase implements IFreeplayVouchersService{
    @observable
    numberOfFreeplayVouchers: number;
    _FPIndicatorDOMRef: HTMLDivElement;

    _receivedVouchersIds = {};

    constructor(serviceFactory: IServiceFactoryExtended){
        super(serviceFactory);

        this.services.sdk.getFreeplayVouchers().then((freePlays) => {
            this.setFreeplayVouchersNumber(freePlays);
        });
        this.subscribe();
    }

    private subscribe() {
        this.services.sdk.events.onFreeplayVouchersUpdated((data: unknown[]) => {
            const numberOfFreeplays = data ? data.length : 0;
            this.setFreeplayVouchersNumber(numberOfFreeplays);

            const _newVouchers = this.extractNewVouchersFromData(data);
            this._removeOldVouchers(data);
            _newVouchers.length > 0 && this._showTooltipOnFreePlay();
        });

        this.services.sdk.events.onOAuthStateChanged(()=>{
            if(!this.services.user.isAuthorized){
                this.services.storage.setItem(FREE_PLAY_TOOLTIP_IS_DISPLAYED__IN_SESSION, false);
            }
        })
    }

    private extractNewVouchersFromData(data) {
        return (data || []).filter(({ID}) => {
            const isAlreadyReceivedVoucher = this._receivedVouchersIds.hasOwnProperty(ID);

            if(!isAlreadyReceivedVoucher){
                this._receivedVouchersIds[ID] = ID;
                return true;
            }

            return false;
        });
    }

    private _removeOldVouchers(data): void {
        for(const ID in this._receivedVouchersIds) {
            const isVoucherExists = data && data.filter((element)=> element.ID === ID).length > 0;
            if(!isVoucherExists){
                delete this._receivedVouchersIds[ID];
            }
        }
    }

    @action
    private setFreeplayVouchersNumber(data: number) {
        this.numberOfFreeplayVouchers = data;
    }

    @action.bound
    private _onTooltipClicked(){
        this.services.analytics.sendAnalytics("Tooltip", "Click", "FP_Bubble");
        this.services.tooltip.closeCurrentTooltip();
        this.services.userMenu.openRewards();
    }

    _shouldShowTooltip(){
        if(!this.hasVouchers || !this._FPIndicatorDOMRef)
            return;

        const storageService = this.services.storage;
        const tooltipDisplayedNumber = parseInt(storageService.getItem(FREE_PLAY_TOOLTIP_DISPLAYED_NUMBER)) || 0;
        const tooltipDisplayedNumberInCurrentSession = storageService.getItem(FREE_PLAY_TOOLTIP_IS_DISPLAYED__IN_SESSION) === "true";

        if(tooltipDisplayedNumber >= 5
            || tooltipDisplayedNumberInCurrentSession
            || !this.services.routing.isOrbitRoute(this.services.location.currentUri.pathName)
            || this.services.routing.currentRoute.data.preventNotifications
            || this.services.sdk.isGameRunning()){
            return false;
        }

        storageService.setItem(FREE_PLAY_TOOLTIP_DISPLAYED_NUMBER, tooltipDisplayedNumber + 1);
        storageService.setItem(FREE_PLAY_TOOLTIP_IS_DISPLAYED__IN_SESSION, true);

        return true;
    }

    // Show tooltip for 7 sec, 5 times only and max 1 time each login session.
    private _showTooltipOnFreePlay(){
        if(!this._shouldShowTooltip()) {
            return;
        }

        const translateService = this.services.translation;
        const FPGiftIcon = this.services.theme.currentTheme.images.tooltip.giftIcon.toString();

        this.services.tooltip.createTooltip(
            <IconTitleTextTooltip icon={FPGiftIcon} title={translateService.translate("CONGRATS")} text={translateService.translate("YOUR_FP_ARE_WAITING")} onClick={this._onTooltipClicked}/>,
            {
                attach: this.services.device.isMobile ? '.cgp-welcome-component' : this._FPIndicatorDOMRef,
                timeout: 7000
            }
        );

        this.services.analytics.sendAnalytics("Tooltip", "Show", "FP_Bubble");
    }

    @action.bound
    setFreePlayIndicatorRef(ref: HTMLDivElement){
        this._FPIndicatorDOMRef = ref;
    }

    @computed
    get hasVouchers() {
        return this.numberOfFreeplayVouchers > 0;
    }

}
